import { Box, Typography, Button } from '@mui/material';
import { useState, useMemo } from 'react';
import Header from '@/Components/MagicLink/Header';
import ParameterItem from '@/Components/MagicLink/ParameterItem';
import SelectUploadOptionBottomSheet from '@/Components/MagicLink/SelectUploadOptionBottomSheet';
import Section from '@/Components/MagicLink/Section';
import MagicLinkActionBar from '@/Components/MagicLink/MagicLinkActionBar';
import { useMagicLinkAppProvider, MagicLinkView } from '@/Provider/MagicLinkAppProvider';
import { magicLinkPageColumnSx, magicLinkPageOuterSx } from '@/Utils/MagicLink/pageLayout';

const ExtractedDataView = () => {
    const { navigateToView, frontSideUploadResult, backSideUploadResult } = useMagicLinkAppProvider();
    const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);

    // Determine which side to display - show the most recently uploaded side
    // If back side exists, show it (it's the second step), otherwise show front side
    const currentResult = useMemo(() => {
        return backSideUploadResult || frontSideUploadResult;
    }, [frontSideUploadResult, backSideUploadResult]);

    const isDisplayingFront = currentResult?.isBackSide === false;
    const isDisplayingBack = currentResult?.isBackSide === true;

    const extractedData = useMemo(() => {
        if (!currentResult?.extractedData) {
            return [];
        }
        return currentResult.extractedData.map(item => [item.label, item.value] as [string, string]);
    }, [currentResult]);

    const uploadInfo = useMemo(() => {
        const bothUploaded = frontSideUploadResult !== null && backSideUploadResult !== null;

        let headline = 'Skontrolujte svoje údaje';
        let buttonText = 'Pokračovať';
        let buttonAction = () => navigateToView(MagicLinkView.ID_CARD_UPLOADED);

        if (bothUploaded) {
            headline = isDisplayingBack ? 'Skontrolujte svoje údaje - Zadná strana' : 'Skontrolujte svoje údaje - Predná strana';
            buttonText = 'Pokračovať';
            buttonAction = () => navigateToView(MagicLinkView.ID_CARD_UPLOADED);
        } else if (isDisplayingFront && frontSideUploadResult?.isBackSide === false) {
            headline = 'Skontrolujte svoje údaje - Predná strana';
            buttonText = 'Nahrať zadnú stranu';
            buttonAction = () => navigateToView(MagicLinkView.CAMERA);
        } else if (isDisplayingBack && backSideUploadResult?.isBackSide === true) {
            headline = 'Skontrolujte svoje údaje - Zadná strana';
            buttonText = 'Nahrať prednú stranu';
            buttonAction = () => navigateToView(MagicLinkView.CAMERA);
        }

        return { headline, buttonText, buttonAction, bothUploaded };
    }, [frontSideUploadResult, backSideUploadResult, isDisplayingFront, isDisplayingBack, navigateToView]);

    return (
        <Box component="main" sx={magicLinkPageOuterSx}>
            <Box sx={magicLinkPageColumnSx}>
                    <Header />

                    {/* ------------------------------------------------------------- */}
                    {/* Extracted ID-card data in a white Section card */}
                    {/* ------------------------------------------------------------- */}
                    <Section
                        title={uploadInfo.headline}
                        headerRight={(
                            <Typography
                                variant="body2"
                                color="error"
                                onClick={() => setIsBottomSheetOpen(true)}
                                sx={{
                                    textDecoration: 'underline',
                                    cursor: 'pointer',
                                    fontWeight: 500,
                                }}
                            >
                                Nahrať znova
                            </Typography>
                        )}
                        disableBodyPadding
                        disableBodyGap
                    >
                        {extractedData.length > 0 ? (
                            extractedData.map(([key, value], index) => {
                                const isLastParam = index === extractedData.length - 1;
                                return (
                                    <ParameterItem
                                        key={index}
                                        label={key}
                                        value={value}
                                        isLast={isLastParam}
                                    />
                                );
                            })
                        ) : (
                            <Box sx={{ padding: 2, textAlign: 'center' }}>
                                <Typography variant="body2" color="text.secondary">
                                    Žiadne údaje na zobrazenie
                                </Typography>
                            </Box>
                        )}
                    </Section>

                    <MagicLinkActionBar
                        primary={
                            <Button
                                variant="contained"
                                fullWidth
                                onClick={uploadInfo.buttonAction}
                                sx={{
                                    textTransform: 'none',
                                    height: '55px',
                                    fontWeight: 500,
                                }}
                            >
                                {uploadInfo.buttonText}
                            </Button>
                        }
                    />
            </Box>

            <SelectUploadOptionBottomSheet
                open={isBottomSheetOpen}
                onClose={() => setIsBottomSheetOpen(false)}
                onTakePhoto={() => navigateToView(MagicLinkView.CAMERA)}
                onSelectFromGallery={() => navigateToView(MagicLinkView.UPLOAD_ID_CARD)}
            />
        </Box>
    );
};

export default ExtractedDataView;
